<template>
  <div
    class="usp-block-background"
    :class="variantClass ? `usp-block-background${variantClass}` : ''"
  >
    <div v-if="title" class="usp-block__title">
      <h2>{{ title }}</h2>
    </div>
    <div
      v-editable="iconText"
      class="usp-block container"
      :class="{
        'usp-block--even-blocks': isEven
      }"
    >
      <div
        class="usp-block__items-wrapper"
        :class="{
          'usp-block__items-wrapper--even-blocks': isEven
        }"
      >
        <div
          v-for="(item, index) in iconText"
          :key="index"
          class="usp-block__item-wrapper"
          :class="{
            'usp-block__item-wrapper--display-dense': displayDense,
            'usp-block__item-wrapper--display-separator': separatorLine
          }"
        >
          <div class="usp-block__item">
            <component
              :is="item.link.cached_url ? 'AppLink' : 'p'"
              v-track="item.tracking"
              :link="item.link.cached_url"
            >
              <div
                class="usp-block__item-container"
                :class="{
                  'usp-block__item-container--column': displayAsColumn
                }"
              >
                <div class="usp-block__item-container-child">
                  <i
                    v-if="item.icon.name"
                    class="usp-block__item-icon icon"
                    :class="item.icon.name"
                  ></i>
                  <img
                    v-else-if="item.image.filename"
                    class="usp-block__item-image"
                    :src="item.image.filename"
                  />
                  <div v-if="!noEllipse" class="usp-block__icon-ellipse"></div>
                </div>
                <div
                  class="usp-block__item-content"
                  :class="{
                    'usp-block__item-content--center-vertically': centerTextVertically
                  }"
                >
                  <div class="usp-block__item-title">
                    {{ item.title }}
                  </div>
                  <div class="usp-block__item-subtitle">
                    {{ item.subtitle }}
                  </div>
                  <div class="usp-block__item-text">
                    {{ item.text }}
                  </div>
                </div>
              </div>
            </component>
          </div>
        </div>
      </div>
      <div v-if="buttonProps" class="usp-block__actions">
        <AppButton
          v-track="buttonProps.tracking"
          class="carousel-bottom__button"
          v-bind="buttonProps"
        >
          {{ buttonProps.label }}
        </AppButton>
      </div>
      <div v-if="bottomArticle.length" class="usp-block__bottom-article">
        <Article v-if="bottomArticle" :text-content="bottomArticleData" />
      </div>
    </div>
  </div>
</template>

<script>
import AppLink from "./AppLink"
import AppButton from "../components/AppButton"
import stubDirectives from "../mixins/stubDirectives"
import buttonProps from "../helpers/buttonProps"
import Article from "./Article.vue"

export default {
  components: {
    AppLink,
    AppButton,
    Article
  },
  mixins: [stubDirectives],
  props: {
    iconText: {
      type: Array,
      required: true
    },
    variant: {
      type: String,
      default: null,
      validator(value) {
        return ["dark", "yellow", "grey", "night"].includes(value)
      }
    },
    displayAsColumn: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    subtitle: {
      type: String,
      default: ""
    },
    noEllipse: {
      type: Boolean,
      default: false
    },
    centerTextVertically: {
      type: Boolean,
      default: false
    },
    displayDense: {
      type: Boolean,
      default: false
    },
    separatorLine: {
      type: Boolean,
      default: false
    },
    button: {
      type: Array,
      default: () => []
    },
    bottomArticle: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    variantClass() {
      switch (this.variant) {
        case "dark":
          return "--is-dark"
        case "yellow":
          return "--is-yellow"
        case "grey":
          return "--is-grey"
        case "night":
          return "--is-night"
        default:
          return ""
      }
    },
    buttonProps() {
      return buttonProps(this.button)
    },
    isEven() {
      return this.iconText.length % 2 === 0
    },
    bottomArticleData() {
      return this.bottomArticle?.[0]?.textContent ?? {}
    }
  }
}
</script>

<style lang="scss" scoped>
.usp-block-background {
  background-color: $background;

  &--is-night {
    background-color: $black;
    color: $white;

    .usp-block__icon-ellipse {
      background: $white;
    }
  }

  &--is-dark {
    background-color: $black;
    color: $white;

    .usp-block__item-container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }

    .usp-block__item-title,
    .usp-block__item-text {
      text-align: left;
    }

    .usp-block__item-title {
      font-size: $font-size-5;
      line-height: $line-height-2;
      margin-top: $space-8;
    }
    .usp-block__item-text {
      margin-top: $space-6;
    }

    .usp-block__icon-ellipse {
      background: $white;
    }
  }

  &--is-yellow {
    background-color: $yellow-400;
    color: $night;
    .usp-block__icon-ellipse {
      background: $night;
    }
  }
  &--is-grey {
    background-color: $grey-200;
    color: $night;
    .usp-block__icon-ellipse {
      background: $night;
    }
  }
}

.usp-block {
  padding: $space-12 $space-0;

  &__items-wrapper {
    @include lg {
      display: flex;
      flex-wrap: wrap;
    }

    &--even-blocks {
      @include md {
        display: flex;
        flex-wrap: wrap;

        .usp-block__item-wrapper {
          width: 50%;
          padding: 0 $space-7;
          margin: $space-10 0;

          &--display-separator {
            border-bottom: 0;
            border-right: 1px solid $night;

            &:nth-child(even) {
              border-right: 0;
            }
          }
        }
        .usp-block__item {
          margin-left: 0;
          margin-right: 0;
          margin-top: -$space-3;
          margin-bottom: -$space-3;
          padding: 0;
        }
      }
      @include lg {
        .usp-block__item-wrapper {
          width: 25%;

          &--display-separator {
            &:nth-child(even) {
              border-right: 1px solid $night;
            }
            &:last-child {
              border-right: 0;
            }
          }
        }
      }
    }
  }

  &__title {
    text-align: center;
    padding-top: $space-16;
    padding-bottom: $space-4;
  }

  &__item-wrapper {
    width: 76px;
    margin: 0 auto $space-9 140px;

    @include lg {
      width: 33.333%;
      padding: 0 $space-7;
      margin: $space-10 0;
    }

    &--display-dense {
      margin: 0 auto 0 140px;

      @include lg {
        margin: $space-10 0;
      }

      .usp-block__item-text {
        margin-bottom: 0;
      }
    }
    &--display-separator {
      border-bottom: 1px solid $night;

      @include lg {
        border-bottom: 0;
        border-right: 1px solid $night;

        &:last-child {
          border-right: 0;
        }
      }
    }
  }

  &__item {
    margin-right: calc(
      -100vw + 216px
    ); // 140 + 76, which means negative margin to fill the remaining space
    margin-left: -140px;
    padding: 0 $space-5;

    @include lg {
      margin-left: 0;
      margin-right: 0;
      margin-top: -$space-3;
      margin-bottom: -$space-3;
      padding: 0;
    }
  }

  &__item-container {
    display: flex;
    justify-content: left;
    align-items: top;

    &--column {
      flex-direction: column;
      align-items: center;

      .usp-block__item-container-child {
        margin: 0;

        & > * {
          margin: auto;
        }
      }

      .usp-block__item-icon {
        margin: auto;
      }

      .usp-block__item-image {
        display: block;
      }

      .usp-block__item-content {
        text-align: center;
      }
    }
    &-child {
      margin-right: $space-8;
    }
  }

  &__item-content {
    text-align: left;

    &--center-vertically {
      display: flex;
      align-items: center;
      padding-bottom: $space-6;
    }
  }

  &__item-title {
    font-size: $font-size-5;
    font-weight: $weight-medium;
  }

  &__item-subtitle {
    font-size: $font-size-5;
    font-weight: $weight-medium;
    line-height: 1;
  }

  &__item-text {
    font-size: $font-size-3;
    margin-top: $space-3;
    margin-bottom: $space-10;

    @include lg {
      margin-bottom: 0;
    }
  }

  &__item-container-child {
    display: block;
    min-width: $space-14;
  }

  &__item-icon {
    font-size: $font-size-9;
  }

  &__item-image {
    display: block;
    margin: auto;
  }

  &__icon-ellipse {
    background: $grey-100;
    width: $space-14;
    height: $space-2;
    border-radius: 50%;
    margin-top: $space-5;
  }

  &__actions {
    display: flex;
    justify-content: center;
    padding-bottom: $space-6;
    margin-top: $space-12;
  }

  &__bottom-article {
    display: flex;
    justify-content: center;
    margin-bottom: $space-10;
  }
}
</style>
